import React from 'react';
import PublicHeader from './PublicHeader';

interface MissionPageProps {
  onNavigateHome?: () => void;
  onNavigateToRules?: () => void;
  onNavigateToConduct?: () => void;
  onNavigateToWaiver?: () => void;
}

const MissionPage: React.FC<MissionPageProps> = ({ onNavigateHome, onNavigateToRules, onNavigateToConduct, onNavigateToWaiver }) => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-blue-100">
      {/* Header */}
      <PublicHeader title="Mission, Values & Vision" />

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
          {/* Our Mission */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-fishing-blue mb-6">Our Mission</h2>
            <p className="text-gray-700 text-lg leading-relaxed">
              Our mission is to foster a strong sense of community and togetherness through the 
              joy of fishing. We aim to create a welcoming space where anglers of all skill levels 
              can connect, share experiences, and enjoy the outdoors. By organizing events, 
              educational programs, and outreach activities, we strive to support local initiatives, 
              promote environmental stewardship, and strengthen the bonds between individuals 
              and their communities through the shared love of fishing.
            </p>
          </section>

          {/* Our Values */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-fishing-blue mb-6">Our Values</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-blue-50 rounded-lg p-6 border-l-4 border-fishing-blue">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-10 w-10 rounded-full bg-fishing-blue text-white">
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">Respect</h3>
                    <p className="text-gray-700 leading-relaxed">
                      We believe in treating everyone with dignity and kindness, creating 
                      an inclusive environment where all voices are heard.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-green-50 rounded-lg p-6 border-l-4 border-fishing-green">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-10 w-10 rounded-full bg-fishing-green text-white">
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">Community</h3>
                    <p className="text-gray-700 leading-relaxed">
                      Our club thrives on the strength of our community. We are 
                      dedicated to building connections and fostering relationships that enrich our 
                      members and the wider community.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-yellow-50 rounded-lg p-6 border-l-4 border-yellow-500">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-10 w-10 rounded-full bg-yellow-500 text-white">
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">A Fair Go</h3>
                    <p className="text-gray-700 leading-relaxed">
                      We are committed to providing equitable opportunities for all 
                      members, ensuring that everyone has the chance to participate and enjoy the 
                      benefits of being part of our club.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-teal-50 rounded-lg p-6 border-l-4 border-teal-600">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="flex items-center justify-center h-10 w-10 rounded-full bg-teal-600 text-white">
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">Stewardship</h3>
                    <p className="text-gray-700 leading-relaxed">
                      We recognize our responsibility to protect and preserve our 
                      natural resources. We promote sustainable fishing practices and actively 
                      engage in initiatives that benefit the environment and the ecosystems we 
                      cherish.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Our Vision */}
          <section>
            <h2 className="text-3xl font-bold text-fishing-blue mb-6">Our Vision</h2>
            <div className="text-gray-700 text-lg leading-relaxed space-y-4">
              <p>
                At Tumbulgum Fishing Club, we envision a vibrant, value-driven community that 
                embraces all individuals—local, interstate, and international—who share our passion 
                for fishing and the great outdoors. Our club serves as a welcoming haven for anglers 
                of all skill levels, promoting the breathtaking beauty of the Tweed region to travellers 
                from near and far.
              </p>
              <p>
                We are committed to fostering strong relationships among our members, 
                encouraging good behaviour, and prioritizing the well-being of our youth. Through 
                engaging educational sessions, we empower our members with the skills to fish 
                responsibly and cultivate a deep appreciation for the stewardship of our rivers and 
                environment.
              </p>
              <p>
                As we grow, we aim to attract like-minded sponsors that align with our values 
                through goodwill and trustworthiness, ensuring that our initiatives benefit not only our 
                members but also the broader community. Together, we will create lasting memories, 
                promote sustainable practices, and inspire a new generation of anglers to cherish 
                and protect the natural beauty of our waterways.
              </p>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12 px-4 mt-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <h3 className="text-xl font-semibold mb-4">Contact Us</h3>
              <p className="text-gray-300">Website: www.tumbyfc.com</p>
              <p className="text-gray-300">Email: fishing@tumbyfc.com</p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2">
                <li><button onClick={onNavigateHome} className="text-gray-300 hover:text-white">Home</button></li>
                <li><button onClick={onNavigateToRules} className="text-gray-300 hover:text-white">Club Rules</button></li>
                <li><button onClick={onNavigateToConduct} className="text-gray-300 hover:text-white">Code of Conduct</button></li>
                <li><button onClick={onNavigateToWaiver} className="text-gray-300 hover:text-white">Waiver & Release</button></li>
                <li><a href="/Constitution-Tumbulgum-Fishing-Club-V5.pdf" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white">Club Constitution</a></li>
                <li><button className="text-gray-300 hover:text-white">Privacy Policy</button></li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-4">Location</h3>
              <p className="text-gray-300">Tumbulgum, New South Wales</p>
              <p className="text-gray-300">Australia</p>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-8 text-center">
            <p className="text-gray-400">&copy; 2026 Tumbulgum Fishing Club. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MissionPage;
